import { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { SlidersHorizontal, Search, Check, ChevronRight, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CustomDatePickerFixed } from './CustomDatePickerFixed';

export const FilterPopover = ({ categories, initialFilters, onApply, accent, align = 'right' }) => {
  const [open, setOpen] = useState(false);
  
  // localFilters: { categoryId: [optionId, optionId] }
  const [localFilters, setLocalFilters] = useState(initialFilters || {});
  
  // which category is currently selected in the left menu
  const [activeCategoryId, setActiveCategoryId] = useState(categories[0]?.id);
  const [searchQuery, setSearchQuery] = useState('');

  const containerRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(e.target);
      const isOutsidePortal = !e.target.closest('[style*="z-index: 99999"]');
      if (open && isOutsideContainer && isOutsidePortal) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  // Reset local state when opened. Depends on `open` alone by design: callers
  // build `categories` inline, so it is a fresh array on every parent render,
  // and the shared store re-renders these screens often. Depending on it (or on
  // `initialFilters`) re-ran this reset mid-use, wiping the user's selections
  // and snapping the left menu back to the first category.
  useEffect(() => {
    if (open) {
      // Drop selections whose option no longer exists. Callers narrow one
      // category's options from another category's applied filter (Sales
      // narrows User by Branch), which otherwise leaves selections the user
      // cannot see, still filtering the results, with no way to untick them.
      const incoming = initialFilters || {};
      const pruned = {};
      categories.forEach((cat) => {
        const val = incoming[cat.id];
        if (val === undefined) return;
        if (Array.isArray(val)) {
          const valid = new Set((cat.options || []).map((o) => o.id));
          pruned[cat.id] = val.filter((id) => valid.has(id));
        } else {
          pruned[cat.id] = val;
        }
      });
      setLocalFilters(pruned);
      setSearchQuery('');
      if (categories.length > 0) setActiveCategoryId(categories[0].id);
    }
  }, [open]);

  const activeCategory = categories.find((c) => c.id === activeCategoryId) || categories[0];

  const visibleOptions = useMemo(() => {
    if (!activeCategory) return [];
    if (!searchQuery.trim()) return activeCategory.options;
    const q = searchQuery.toLowerCase();
    return activeCategory.options.filter((opt) => (opt.label || '').toLowerCase().includes(q));
  }, [activeCategory, searchQuery]);

  const toggleOption = (optId) => {
    setLocalFilters((prev) => {
      if (activeCategory?.type === 'single') {
        return { ...prev, [activeCategoryId]: [optId] };
      }
      const current = prev[activeCategoryId] || [];
      const updated = current.includes(optId)
        ? current.filter((id) => id !== optId)
        : [...current, optId];
      return { ...prev, [activeCategoryId]: updated };
    });
  };

  const handleDateChange = (field, value) => {
    setLocalFilters((prev) => {
      const current = prev[activeCategoryId] || {};
      return { ...prev, [activeCategoryId]: { ...current, [field]: value } };
    });
  };

  const handleClear = () => {
    const empty = {};
    categories.forEach((c) => { 
      if (c.type === 'single') {
        empty[c.id] = localFilters[c.id] || [];
      } else {
        empty[c.id] = c.type === 'date_range' ? {} : []; 
      }
    });
    setLocalFilters(empty);
  };

  const handleApply = () => {
    onApply(localFilters);
    setOpen(false);
  };

  const activeFilterCount = Object.entries(initialFilters || {}).reduce((acc, [key, val]) => {
    if (key === 'view') return acc; // Don't count the main tab view as a filter
    if (Array.isArray(val)) return acc + val.length;
    if (val && typeof val === 'object') {
      let count = 0;
      if (val.from) count++;
      if (val.to) count++;
      return acc + count;
    }
    return acc;
  }, 0);

  return (
    <div className="relative inline-block text-left" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center justify-center gap-2 p-2.5 rounded-lg text-white shadow-sm transition-transform active:scale-95"
        style={{ background: accent }}
      >
        <SlidersHorizontal size={15} />
        {activeFilterCount > 0 && (
          <span className="bg-white text-[#0d0d0d] w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm">
            {activeFilterCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute ${align === 'right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'} mt-2 w-[480px] max-w-[90vw] bg-white rounded-2xl shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] border border-[var(--theme-tertiary)] z-[100] flex flex-col overflow-hidden`}
          >
            <div className="flex flex-col sm:flex-row min-h-[200px] max-h-[50vh]">
              {/* Left sidebar: Categories */}
              <div className="w-[160px] border-r border-[var(--theme-tertiary)] bg-gray-50 flex flex-col overflow-y-auto custom-scrollbar shrink-0">
                  {categories.map((cat) => {
                    const isSelected = cat.id === activeCategoryId;
                    const val = localFilters[cat.id];
                    const count = Array.isArray(val) ? val.length : (val ? (val.from ? 1 : 0) + (val.to ? 1 : 0) : 0);
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => {
                          setActiveCategoryId(cat.id);
                          setSearchQuery('');
                        }}
                        className={`relative flex items-center justify-between px-4 py-3 text-[12px] font-bold text-left transition-colors ${
                          isSelected 
                            ? 'bg-white text-[#0d0d0d]' 
                            : 'text-slate-500 hover:bg-gray-100 hover:text-slate-700'
                        }`}
                      >
                        {isSelected && (
                          <span
                            className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full"
                            style={{ background: accent }}
                            aria-hidden="true"
                          />
                        )}
                        <span className="truncate flex-1">
                          {cat.label}
                          {count > 0 && (
                            <span className="ml-1.5 inline-block text-[10px] font-extrabold text-white px-1.5 py-0.5 rounded-md" style={{ background: accent }}>
                              {count}
                            </span>
                          )}
                        </span>
                        {isSelected && <ChevronRight size={14} className="shrink-0" style={{ color: accent }} />}
                      </button>
                    );
                  })}
                </div>

              {/* Right panel: Search and Content */}
              <div className="flex-1 flex flex-col bg-white overflow-hidden">
                {activeCategory?.type === 'date_range' ? (
                  <div className="p-5 flex flex-col gap-5">
                    <div className="flex flex-col gap-2">
                      <label className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500">From Date</label>
                      <CustomDatePickerFixed 
                        value={localFilters[activeCategoryId]?.from || ''} 
                        onChange={(v) => handleDateChange('from', v)} 
                        className="!rounded-xl border border-[var(--theme-tertiary)]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500">To Date</label>
                      <CustomDatePickerFixed 
                        value={localFilters[activeCategoryId]?.to || ''} 
                        onChange={(v) => handleDateChange('to', v)} 
                        className="!rounded-xl border border-[var(--theme-tertiary)]"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="px-4 py-3 border-b border-[var(--theme-tertiary)] shrink-0">
                      <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder={`Search ${activeCategory?.label.toLowerCase()}…`}
                          className="w-full bg-[var(--theme-secondary)] rounded-lg pl-9 pr-3 py-2 text-xs font-medium text-[#0d0d0d] border border-transparent focus:border-[var(--theme-primary)] focus:bg-white outline-none transition-colors"
                        />
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                      {visibleOptions.length === 0 ? (
                        <div className="px-3 py-8 text-center text-[11px] font-medium text-slate-400">
                          No options found.
                        </div>
                      ) : (
                        visibleOptions.map((opt) => {
                          const checked = (localFilters[activeCategoryId] || []).includes(opt.id);
                          return (
                            <div
                              key={opt.id}
                              onClick={() => toggleOption(opt.id)}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-[var(--theme-secondary)] transition-colors group"
                            >
                              <div 
                                className={`w-4 h-4 rounded-[4px] border flex items-center justify-center shrink-0 transition-colors ${
                                  checked 
                                    ? 'border-transparent text-white' 
                                    : 'border-slate-300 bg-white group-hover:border-slate-400'
                                }`}
                                style={{ background: checked ? accent : undefined }}
                              >
                                {checked && <Check size={12} strokeWidth={3} />}
                              </div>
                              <span className="text-[12px] font-semibold text-[#0d0d0d] truncate select-none">
                                {opt.label}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-[var(--theme-tertiary)] bg-gray-50 flex items-center justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={handleClear}
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-[11px] font-extrabold uppercase tracking-widest text-slate-600 transition-colors"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="px-5 py-2 rounded-lg text-white text-[11px] font-extrabold uppercase tracking-widest shadow-sm transition-opacity hover:opacity-90"
                style={{ background: accent }}
              >
                Apply
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

FilterPopover.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  initialFilters: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
  accent: PropTypes.string.isRequired,
};
